export const OPEN_CASH_DRAWER_TEMPLATE_TYPE = 'open_cash_drawer' as const;

export type PrinterTemplateType =
  | 'tab_check'
  | 'payment_receipt'
  | 'test_page'
  | 'dispatch_order'
  | 'dispatch_cancellation'
  | 'cash_balance'
  | 'cash_withdrawal'
  | typeof OPEN_CASH_DRAWER_TEMPLATE_TYPE;

export interface PrinterTemplate {
  commands: PrinterCommand[];
  metadata: {
    type: PrinterTemplateType;
    timestamp: Date;
  };
}

export interface PrinterCommand {
  method: string;
  params: unknown[];
}

/** Duties a printer can handle. A single printer may serve multiple duties. */
export type PrinterDuty = 'kitchen' | 'bar' | 'cashier';
export type PrinterBackend = 'generic_escpos' | 'usb_android' | 'usb_windows';

export interface PrinterConfig {
  id: string;
  name: string;
  /** Duties this printer is responsible for handling */
  duties: PrinterDuty[];

  // Connection settings used when mapping a network cashier profile into a template config
  ip: string;
  port: number;
  timeout: number;

  // Template settings (printer-specific)
  paperWidth: number;
  fontSize: {
    header: number;
    normal: number;
    small: number;
  };
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  language: 'es' | 'en';
  currency: string;
}

/** Stored org setting from the removed ePOS backend. Kept so settings writes do not drop it. */
export interface EpsonPrintersConfig {
  enabled: boolean;
  printers: PrinterConfig[];
}

export interface GenericEscposPrinterConfig {
  id: string;
  name: string;
  duties: PrinterDuty[];
  host: string;
  port: number;
  timeout: number;
  codePage: 'cp437' | 'cp858' | 'utf8';
  openCashDrawer: boolean;
  printDispatchCopy?: boolean;
  language: 'es' | 'en';
  currency: string;
}

export interface GenericEscposPrintersConfig {
  enabled: boolean;
  printers: GenericEscposPrinterConfig[];
}

export interface UsbAndroidPrinterConfig {
  id: string;
  name: string;
  duties: PrinterDuty[];
  vendorId?: number;
  productId?: number;
  codePage: 'cp437' | 'cp858' | 'utf8';
  openCashDrawer: boolean;
  printDispatchCopy?: boolean;
  language: 'es' | 'en';
  currency: string;
}

export interface UsbAndroidPrintersConfig {
  enabled: boolean;
  printers: UsbAndroidPrinterConfig[];
}

export interface UsbWindowsPrinterConfig {
  id: string;
  name: string;
  duties: PrinterDuty[];
  /** Installed Windows printer name (from Printers & scanners) */
  printerName: string;
  codePage: 'cp437' | 'cp858' | 'utf8';
  openCashDrawer: boolean;
  printDispatchCopy?: boolean;
  language: 'es' | 'en';
  currency: string;
}

export interface UsbWindowsPrintersConfig {
  enabled: boolean;
  printers: UsbWindowsPrinterConfig[];
}

export interface PrinterRoutingConfig {
  preferredBackend: PrinterBackend;
}
